import { useState } from 'react';
import cx from 'classnames';

import { IconArrowUp_16 } from 'src/assets/icons/16x16/IconArrowUp_16';
import { CustomIconButton } from 'src/components/CustomIconButton';
import { type SavedDocumentComment, type SavedDocumentThread } from 'src/data/savedDocument';
import { type CallbackN, type VoidFunction } from 'src/lib/types';

import { CommentInput } from './CommentInput';
import { CommentItem } from './CommentItem';
import styles from './ThreadCard.module.scss';

interface ThreadCardProps {
  className?: string;
  thread: SavedDocumentThread;
  active: boolean;
  replying: boolean;
  onFocus: VoidFunction;
  // Resolves with `true` once the reply is posted, so a failed one keeps its text.
  onReply: (text: string) => Promise<boolean>;
  onResolve: VoidFunction;
  onEditComment: CallbackN<[string, string]>;
  onDeleteComment: CallbackN<[SavedDocumentComment]>;
}

export const ThreadCard = ({
  className,
  thread,
  active,
  replying,
  onFocus,
  onReply,
  onResolve,
  onEditComment,
  onDeleteComment,
}: ThreadCardProps) => {
  const [reply, setReply] = useState('');

  const submitReply = async () => {
    if (reply.trim() && !replying && (await onReply(reply.trim()))) {
      setReply('');
    }
  };

  return (
    <div
      className={cx(styles.root, className, { [styles.active]: active })}
      onMouseDown={onFocus}
      data-testid="savedDocumentThread"
    >
      {thread.comments.map((comment, index) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          onResolve={index === 0 ? onResolve : undefined}
          onEdit={(text) => onEditComment(comment.id, text)}
          onDelete={() => onDeleteComment(comment)}
        />
      ))}

      {active && (
        <div className={styles.reply}>
          <CommentInput
            className={styles.replyInput}
            value={reply}
            placeholder="Reply"
            onChange={setReply}
            onSubmit={submitReply}
            dataTestId="savedDocumentReplyInput"
          />
          {reply.trim() && (
            <CustomIconButton
              className={styles.send}
              variant="primary"
              size="small"
              loading={replying}
              disabled={replying}
              onClick={submitReply}
            >
              <IconArrowUp_16 />
            </CustomIconButton>
          )}
        </div>
      )}
    </div>
  );
};
